export interface Coordinates {
  latitude: number;
  longitude: number;
}

export const LEAFLET_MARKER_ICON_URL =
  'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png';

export function hasCoordinates(value: unknown): value is Coordinates {
  if (typeof value !== 'object' || value === null) return false;
  const coords = value as Partial<Coordinates>;
  return Number.isFinite(coords.latitude) && Number.isFinite(coords.longitude);
}

export function staticMapUrl(
  latitude: number,
  longitude: number,
  zoom = 14,
  width = 640,
  height = 320
): string {
  void width;
  void height;

  const safeLatitude = Math.max(Math.min(latitude, 85.05112878), -85.05112878);
  const normalizedLongitude = ((((longitude + 180) % 360) + 360) % 360) - 180;
  const tilesPerAxis = 2 ** zoom;

  const x = Math.floor(((normalizedLongitude + 180) / 360) * tilesPerAxis);
  const latitudeRadians = (safeLatitude * Math.PI) / 180;
  const y = Math.floor(
    ((1 -
      Math.log(Math.tan(latitudeRadians) + 1 / Math.cos(latitudeRadians)) /
        Math.PI) /
      2) *
      tilesPerAxis
  );

  return `https://tile.openstreetmap.org/${zoom}/${x}/${y}.png`;
}

export function googleMapsUrl(latitude: number, longitude: number): string {
  return `https://www.google.com/maps?q=${latitude},${longitude}`;
}

export function openStreetMapUrl(
  latitude: number,
  longitude: number,
  zoom = 16
): string {
  const params = new URLSearchParams({
    mlat: String(latitude),
    mlon: String(longitude),
  });
  return `https://www.openstreetmap.org/?${params.toString()}#map=${zoom}/${latitude}/${longitude}`;
}

export function openStreetMapEmbedUrl(
  coordinates: Array<{ latitude: number; longitude: number }>
): string | null {
  if (coordinates.length === 0) return null;

  const latitudes = coordinates.map((coord) => coord.latitude);
  const longitudes = coordinates.map((coord) => coord.longitude);

  const minLatitude = Math.min(...latitudes);
  const maxLatitude = Math.max(...latitudes);
  const minLongitude = Math.min(...longitudes);
  const maxLongitude = Math.max(...longitudes);

  const latitudePadding = Math.max((maxLatitude - minLatitude) * 0.2, 0.01);
  const longitudePadding = Math.max((maxLongitude - minLongitude) * 0.2, 0.01);

  const left = minLongitude - longitudePadding;
  const right = maxLongitude + longitudePadding;
  const bottom = minLatitude - latitudePadding;
  const top = maxLatitude + latitudePadding;

  const bbox = `${left},${bottom},${right},${top}`;
  const query = new URLSearchParams({
    bbox,
    layer: 'mapnik',
  });

  for (const coordinate of coordinates.slice(0, 50)) {
    query.append('marker', `${coordinate.latitude},${coordinate.longitude}`);
  }

  return `https://www.openstreetmap.org/export/embed.html?${query.toString()}`;
}

export function openStreetMapSingleEmbedUrl(
  latitude: number,
  longitude: number
): string {
  const latPad = 0.006;
  const lonPad = 0.01;
  const left = longitude - lonPad;
  const right = longitude + lonPad;
  const bottom = latitude - latPad;
  const top = latitude + latPad;
  const bbox = `${left},${bottom},${right},${top}`;
  const query = new URLSearchParams({ bbox, layer: 'mapnik' });
  query.append('marker', `${latitude},${longitude}`);
  return `https://www.openstreetmap.org/export/embed.html?${query.toString()}`;
}
